export default {
  "slug": "fire-alarm-system-design-basics",
  "title": "Fire Alarm System Design Basics",
  "pdf_url": "/guide-pdfs/fire-alarm-system-design-basics.pdf",
  "description": "A beginner's guide to fire alarm system design: the core components, how detection becomes notification, and the design principles behind a reliable layout.",
  "keywords": "fire alarm system design, fire alarm training, fire alarm components, fire alarm layout",
  "ogImage": "/guide-og/fire-alarm-system-design-basics.jpg",
  "faqs": [
    {
      "q": "What are the main components of a fire alarm system?",
      "a": "A fire alarm system combines a control panel (the brain), initiating devices like smoke and heat detectors and pull stations, notification appliances such as horns and strobes, a power supply with battery backup, and the wiring pathways that connect them."
    },
    {
      "q": "Where do I start when designing a fire alarm system?",
      "a": "Start from the building's use and hazards — not a template. Decide what to detect, how to notify occupants, and how everything connects, then document it to the adopted code edition so it can be installed, tested, and inspected."
    },
    {
      "q": "Is this a substitute for a licensed fire alarm design?",
      "a": "No. This is an educational trainer. Every real design must be verified against the currently adopted code edition and approved by a licensed professional and your Authority Having Jurisdiction (AHJ)."
    }
  ],
  "category": "Design",
  "readMins": 6,
  "published": "2026-08-17",
  "updated": "2026-09-27",
  "hero": "https://static.prod-images.emergentagent.com/jobs/73bed830-7de9-440e-a664-81f833da5bb8/images/7d1af0704329b7454e81dddae39de0951233c2af152d4fc2713e8fd13bdd4c1f.jpeg",
  "heroAlt": "Illustration of a fire alarm floor plan with device layout",
  "excerpt": "Understand the building blocks of fire alarm system design and how a good layout turns detection into fast, clear notification.",
  "sections": [
    {
      "h": "What is fire alarm system design?",
      "p": "Fire alarm system design is the process of deciding what to detect, how to warn people, and how everything connects — then documenting it so it can be installed, tested, and inspected. Good design starts from the building's use and hazards, not from a template."
    },
    {
      "h": "The core components",
      "p": "Every system combines a control panel (the brain), initiating devices (inputs such as smoke detectors, heat detectors, and pull stations), notification appliances (outputs such as horns and strobes), a power supply with battery backup, and the pathways that connect them. Learn each part in depth in our Learning Center."
    },
    {
      "h": "From detection to notification",
      "p": "When an initiating device senses a condition, it reports to the panel, which evaluates its programmed sequence of operation and activates outputs — sounding alarms, notifying a monitoring service, and often triggering elevator recall or HVAC shutdown."
    },
    {
      "h": "Design principles that matter",
      "p": "Match detection type to the environment, plan notification for audibility and visibility, respect circuit limits and voltage drop, and always design to the adopted code edition. When in doubt, coordinate early with the Authority Having Jurisdiction (AHJ)."
    }
  ],
  "howTo": [
    {
      "name": "Define the scope from the building",
      "text": "Identify the occupancy, hazards, and what must be detected and protected. The building's use — not a template — drives every later decision."
    },
    {
      "name": "Choose detection",
      "text": "Select detector types that match each space (photoelectric, ionization, heat, or multi-criteria) and place them to detect real fires while avoiding nuisance alarms."
    },
    {
      "name": "Plan notification",
      "text": "Lay out horns and strobes so the alarm is audible and visible to all occupants throughout occupiable areas, not just corridors."
    },
    {
      "name": "Lay out circuits and power",
      "text": "Assign devices to the correct circuits (IDC, NAC, SLC), size conductors for voltage drop, and provide a power supply with battery backup."
    },
    {
      "name": "Document and verify",
      "text": "Produce the drawings, device legend, sequence of operation, and battery/voltage-drop calculations, then verify everything against the adopted code edition and your AHJ."
    }
  ],
  "relatedModules": [
    "design-principles",
    "basics",
    "detector-tech"
  ],
  "relatedGuides": [
    "nfpa-72-explained",
    "smoke-vs-heat-detectors"
  ]
};
